import { useState, useEffect, useCallback, useMemo } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Settings, LogOut, X, Shield, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { getInitials } from '@/lib/utils'
import type { User } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'

interface MobileNavProps {
  open: boolean
  onClose: () => void
}

import { visibleNavGroups, useNavSections } from '@/lib/nav'
import TeamSeatLinks from '@/components/layout/TeamSeatLinks'

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return String(n)
}

export default function MobileNav({ open, onClose }: MobileNavProps) {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [currentUser, setCurrentUser] = useState<User | null>(null)

  useEffect(() => {
    api.getMe()
      .then((result) => {
        if (result?.user) setCurrentUser(result.user as unknown as User)
      })
      .catch(() => {})
  }, [])

  const isAdmin = currentUser?.role === 'admin'
  const visibleGroups = useMemo(() => visibleNavGroups(isAdmin), [isAdmin])
  const { isOpen: isSectionOpen, toggle: toggleSection } = useNavSections(pathname, visibleGroups)

  const tokenUsed = currentUser?.org.tokensUsed ?? 0
  const tokenLimit = currentUser?.org.tokensLimit ?? 1
  const tokenPercent = Math.round((tokenUsed / tokenLimit) * 100)

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [open, handleEscape])

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/60 transition-opacity duration-300 md:hidden',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <nav
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-zinc-900 border-r border-zinc-800 shadow-2xl transition-transform duration-300 ease-in-out md:hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-label="Mobile navigation"
      >
        {/* Header */}
        <div className="flex items-center justify-between h-14 px-4 shrink-0 border-b border-zinc-800">
          <NavLink to="/home" className="flex items-center" onClick={onClose} aria-label="Forge — home">
            <Logo variant="wordmark" size="sm" />
          </NavLink>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-all duration-200"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation links — grouped to mirror desktop sidebar. */}
        <div className="flex-1 overflow-auto py-3 px-3 space-y-3">
          {visibleGroups.map((group, gi) => {
            const isCollapsible = group.collapsible
            const defaultOpen = group.defaultOpen !== false
            const isOpen = isCollapsible ? isSectionOpen(group.label, defaultOpen) : true
            return (
              <div key={group.label || `group-${gi}`}>
                {group.label && (
                  isCollapsible ? (
                    <button
                      type="button"
                      onClick={() => toggleSection(group.label, defaultOpen)}
                      aria-expanded={isOpen}
                      className="flex items-center justify-between w-full px-3 py-1.5 mb-0.5 rounded-md hover:bg-zinc-800/30 transition-colors active:scale-[0.99]"
                    >
                      <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                        {group.label}
                      </span>
                      <ChevronDown
                        className={cn(
                          'w-3.5 h-3.5 text-zinc-500 transition-transform duration-200',
                          !isOpen && '-rotate-90'
                        )}
                      />
                    </button>
                  ) : (
                    <span className="px-3 mb-1.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider block">
                      {group.label}
                    </span>
                  )
                )}
                <div
                  className={cn(
                    'flex flex-col gap-0.5 transition-all duration-200 overflow-hidden',
                    isCollapsible && !isOpen && 'max-h-0 opacity-0',
                    isCollapsible && isOpen && 'max-h-[1000px] opacity-100',
                  )}
                >
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-all duration-200 relative active:scale-[0.98]',
                          isActive
                            ? 'text-brand-500 bg-brand-500/5'
                            : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <div className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r bg-brand-500" />
                          )}
                          <item.icon className="w-5 h-5 shrink-0" />
                          <span>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
                {gi === 0 && (
                  <div className="mt-3">
                    <TeamSeatLinks isAdmin={isAdmin} onNavigate={onClose} />
                  </div>
                )}
              </div>
            )
          })}

          {/* Token usage */}
          <div className="mt-6 px-3">
            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider block mb-2">
              Tokens
            </span>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-zinc-400">
                {formatTokenCount(tokenUsed)} / {formatTokenCount(tokenLimit)}
              </span>
            </div>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  tokenPercent > 80 ? 'bg-brand-600' : 'bg-brand-500'
                )}
                style={{ width: `${tokenPercent}%` }}
              />
            </div>
          </div>

        </div>

        {/* User section */}
        <div className="shrink-0 border-t border-zinc-800 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-brand-500 text-zinc-950 text-xs font-semibold shrink-0">
              {getInitials(currentUser?.name ?? 'U')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-200 truncate">{currentUser?.name ?? 'User'}</p>
              <p className="text-xs text-zinc-500 capitalize">{currentUser?.plan ?? ''}</p>
            </div>
          </div>
          <button
            onClick={() => {
              onClose()
              navigate('/settings')
            }}
            className="flex items-center gap-3 w-full rounded-lg px-4 py-2.5 text-sm text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 transition-all duration-200 active:scale-[0.98]"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                onClose()
                navigate('/admin')
              }}
              className="flex items-center gap-3 w-full rounded-lg px-4 py-2.5 text-sm text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 transition-all duration-200 active:scale-[0.98]"
            >
              <Shield className="w-4 h-4" />
              Admin Settings
            </button>
          )}
          <button
            onClick={() => {
              onClose()
              signOut()
            }}
            className="flex items-center gap-3 w-full rounded-lg px-4 py-2.5 text-sm text-red-400 hover:bg-zinc-800/50 hover:text-red-300 transition-all duration-200 active:scale-[0.98]"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </nav>
    </>
  )
}
